import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth, setSessionTokens } from "@/context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("processing"); // processing | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Emergent OAuth returns session_id in the URL hash after redirect.
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace("#", "?"));
    const sessionId = params.get("session_id");

    // If we're here with no session_id at all, the OAuth handshake did not
    // complete (e.g. user cancelled, browser blocked the popup, or the
    // provider returned an error). Send them back to the login page with a
    // friendly notice rather than silently.
    if (!sessionId) {
      navigate("/login?auth_error=missing_session", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await API.post("/auth/google", { session_id: sessionId });
        // CRITICAL: sync module-level tokens BEFORE checkAuth() runs. Without this
        // the axios request interceptor sends no Authorization header on /auth/me,
        // /me returns 401, checkAuth clears user, ProtectedRoute bounces to /login
        // — the "have to click Continue with Google twice" bug.
        if (data.access_token) {
          setSessionTokens(data.access_token, data.refresh_token);
        }
        await checkAuth();
        // Notify the extension / mobile WebView so the auth-bridge syncs instantly
        try { window.postMessage({ source: "buddy-web", type: "BUDDY_AUTH_CHANGED", action: "google" }, "*"); } catch (_) {}
        // Clear the hash so a browser back button doesn't replay the session
        try { window.history.replaceState(null, "", "/app"); } catch (_) {}
        navigate("/app", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail?.message || "Google sign-in didn't complete. Please try again.");
        setErrorMsg(msg);
        setStatus("error");
      }
    })();
  }, [navigate, checkAuth]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
        <div className="w-full max-w-[420px] buddy-card p-8 text-center" data-testid="auth-callback-error">
          <h1 className="font-display text-2xl font-semibold" style={{ color: "var(--rolex-green)" }}>Buddy</h1>
          <h2 className="font-display text-lg font-medium mt-4" style={{ color: "var(--text-primary)" }}>Sign-in couldn't complete</h2>
          <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>{errorMsg}</p>
          <button onClick={() => navigate("/login", { replace: true })} className="btn-primary w-full py-2.5 text-sm" data-testid="auth-callback-retry">
            Try signing in again
          </button>
          <p className="text-xs mt-4" style={{ color: "var(--text-light)" }}>
            If this keeps happening, try email / password sign-in instead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--surface-cream)" }}>
      <div className="text-center">
        <h1 className="font-display text-2xl font-semibold" style={{ color: "var(--rolex-green)" }}>Buddy</h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Signing you in...</p>
        <div className="mt-4 w-8 h-8 border-3 rounded-full animate-spin mx-auto" style={{ borderColor: "var(--rolex-green)", borderTopColor: "transparent" }} />
      </div>
    </div>
  );
}
